import { useMemo } from 'react';

import cn from 'classnames';
import { IconButton } from '@mui/material';

import { MeetingList } from './partials';
import { ViewToggle } from '@src/components';
import { OButton, OCustomCalendar, OResponsiveModal, OSelect, OSwipeableDrawer, OTab, OTooltip } from '@src/shared';

import useScheduledMeetings from './useScheduledMeetings';
import { copyMessageToClipboard, isWebView } from '@src/helpers';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';
import { EWebViewEvents } from '@src/bridge/types';

import { FiSearch } from 'react-icons/fi';
import { LuCalendarDays } from 'react-icons/lu';
import { MdContentCopy } from 'react-icons/md';
import { FaCheckCircle } from 'react-icons/fa';

import { DATE_BANNER_OPTIONS, DATE_OPTIONS, TYPE_FILTER_OPTIONS } from './scheduledMeetingsView.constants';

import styles from './scheduledMeetingsView.module.scss';

const ScheduledMeetingsView = () => {
  const {
    isLoading,
    selectedTypeFilter,
    selectedTab,
    view,
    filteredMeetings,
    groupedMeetings,
    isMobile,
    setView,
    onTabChange,
    setSelectedTypeFilter,
    tempSelectedTypeFilter,
    setTempSelectedTypeFilter,
    isFilterModalOpen,
    setIsFilterModalOpen,
    isModalOpen,
    selectedDate,
    meetingLink,
    selectedMeeting,
    showDatePicker,
    dropdownPosition,
    setIsModalOpen,
    handleDateChange,
    setShowDatePicker,
    handleDatePickerToggle,
    handleResetCustomDate,
    onInvite,
  } = useScheduledMeetings();

  const CalendarComponent = useMemo(
    () => (
      <OCustomCalendar
        value={selectedDate}
        onChange={(value) => handleDateChange(value)}
        onApply={() => setShowDatePicker(false)}
        onClose={() => setShowDatePicker(false)}
        minDate={new Date()}
        isMobile={isMobile}
      />
    ),
    [selectedDate, handleDateChange, setShowDatePicker, isMobile]
  );

  const FilterModal = useMemo(
    () => (
      <OResponsiveModal isOpen={isFilterModalOpen} title="Show" onClose={() => setIsFilterModalOpen(false)}>
        <div className={styles.modalContent}>
          <ul className={styles.optionsList}>
            {TYPE_FILTER_OPTIONS.map((option) => (
              <li
                key={option.value}
                className={cn(styles.optionItem, {
                  [styles.selected]: tempSelectedTypeFilter === option.value,
                })}
                onClick={() => setTempSelectedTypeFilter(option.value)}
              >
                <span>{option.label}</span>
                {tempSelectedTypeFilter === option.value && <FaCheckCircle color="#0088DD" size={20} />}
              </li>
            ))}
          </ul>

          <OButton
            onClick={() => {
              setSelectedTypeFilter(tempSelectedTypeFilter);
              setIsFilterModalOpen(false);
            }}
            text="Apply"
            size="full"
            variant="primary"
          />
        </div>
      </OResponsiveModal>
    ),
    [isFilterModalOpen, setIsFilterModalOpen, setSelectedTypeFilter, setTempSelectedTypeFilter, tempSelectedTypeFilter]
  );

  return (
    <div className={`${styles.scheduledMeetingsContainer} scheduledContainer ${isWebView() ? styles.isWebView : ''}`}>
      <div className={styles.scheduledMeetingsHeading}>
        <div className={styles.headingText}>
          <h1>My Events</h1>
          <p>{new Date().toLocaleDateString('en-US', DATE_OPTIONS)}</p>
        </div>
        {/*TODO: the calendar button will be handled in another ticket */}
        {/* <OButton
          id="my-calender-button"
          onClick={() => []}
          text="My Calender"
          outlined
          variant="secondary"
          size="m-small"
          icon={<CiCalendar size={20} color="#002237" />}
          classes={{ container: styles.calendarBtn }}
        /> */}
      </div>
      <div className={styles.scheduledMeetingsControls}>
        <div className={styles.leftControls}>
          {selectedTab === 'customDate' && selectedDate?.length ? (
            <div className={styles.customDateBanner}>
              <div className={styles.dateBlock}>
                <LuCalendarDays size={16} color="#002237" />
                <span>
                  {selectedDate[0].toLocaleDateString('en-US', DATE_BANNER_OPTIONS)}
                  {`${selectedDate[1] ? ` - ${selectedDate[1].toLocaleDateString('en-US', DATE_BANNER_OPTIONS)}` : ''}`}
                </span>
              </div>

              <button
                id="reset-custom-date-button"
                type="button"
                className={styles.resetButton}
                onClick={handleResetCustomDate}
              >
                Reset
              </button>
            </div>
          ) : (
            <OTab tabs={['today', 'tomorrow', 'later']} activeTab={selectedTab} onTabChange={onTabChange} />
          )}
          {selectedTab !== 'customDate' && (
            <div style={{ position: 'relative', width: '100%' }}>
              <OButton
                id="custom-date-button"
                onClick={(e) => handleDatePickerToggle(e.currentTarget)}
                text="Custom Date"
                outlined
                variant="secondary"
                size={isMobile ? 'full' : 'm-small'}
                icon={<LuCalendarDays size={16} color="#002237" />}
                classes={{ container: styles.calendarBtn }}
              />

              {showDatePicker && !isMobile && (
                <div
                  style={{
                    position: 'absolute',
                    [dropdownPosition === 'top' ? 'bottom' : 'top']: '100%',
                    left: 0,
                    zIndex: 999,
                    backgroundColor: 'var(--bg-color)',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                    borderRadius: '8px',
                    marginTop: dropdownPosition === 'top' ? undefined : '8px',
                    marginBottom: dropdownPosition === 'top' ? '8px' : undefined,
                  }}
                >
                  {CalendarComponent}
                </div>
              )}

              {isMobile && (
                <OSwipeableDrawer isOpen={showDatePicker} onClose={() => setShowDatePicker(false)}>
                  {CalendarComponent}
                </OSwipeableDrawer>
              )}
            </div>
          )}
        </div>
        <div className={styles.rightControls}>
          {/* Search bar */}
          <div className={styles.searchBar}>
            <div className={styles.iconContainer}>
              <FiSearch color="#CCD3D7" size={20} />
            </div>
            <input
              id="search-input"
              type="text"
              placeholder="Search events..."
              value={''}
              onChange={() => {}}
              className={styles.searchInput}
            />
          </div>

          {/* Dropdown for meeting type */}
          <OSelect
            items={TYPE_FILTER_OPTIONS}
            selectedValue={selectedTypeFilter}
            onChange={setSelectedTypeFilter}
            onClick={() => {
              setTempSelectedTypeFilter(selectedTypeFilter);
              setIsFilterModalOpen(true);
            }}
            label="Show:"
            shouldDisableDropdown={isMobile}
            size={isMobile ? 'full' : 'small'}
            autoWidth
            tooltipContent="Filter by meeting type"
            classes={{
              customTextSize: 'custom-text-size',
              customBorderColor: 'var(--border-color)',
              customMenuBackground: 'custom-menu-background scheduled-meeting-select-menu',
              customInput: 'custom-input',
            }}
          />

          {/*list or grid view toggle */}
          <ViewToggle view={view} setView={setView} />
        </div>
      </div>
      <MeetingList
        filteredMeetings={filteredMeetings ?? []}
        groupedMeetings={groupedMeetings}
        isLoading={isLoading}
        selectedTab={selectedTab}
        view={view}
        isModalOpen={isModalOpen}
        selectedMeeting={selectedMeeting}
        onInvite={onInvite}
      />
      <OResponsiveModal
        isOpen={isModalOpen}
        title="Invite to webinar"
        onClose={() => setIsModalOpen(false)}
        modalClasses={{
          container: styles.modalContainer,
          body: styles.modalBody,
          header: styles.modalHeader,
          closeIconWrapper: styles.modalCloseIconWrapper,
        }}
        hasBackdrop
      >
        <div className={styles.modalContent} id="webinar-invite-modal">
          <p className={styles.modalDescription} id="modal-description">
            Share this with attendees you'd like to connect with during the webinar.
          </p>

          <div className={styles.buttonGroup} id="button-group">
            <OButton
              text="Share Room"
              size="medium"
              onClick={() => {
                if (selectedMeeting && meetingLink) {
                  sendEventToWebView({
                    eventType: EWebViewEvents.SHARE_TEXT,
                    payload: {
                      title: 'Share Webinar Link',
                      extraTitle: 'Join our Webinar on v',
                      text: `Webinar link: ${meetingLink}`,
                    },
                  });
                  navigator.share?.({ url: meetingLink });
                }
              }}
              classes={{ container: styles.shareRoomButton }}
              id="share-room-button"
            />

            <OButton
              text="Mail Invitation"
              size="medium"
              onClick={() => {}}
              id="mail-invitation-button"
              classes={{ container: styles.mailInvitationButton }}
              toolTipContent="Coming soon"
            />
          </div>
          {/* Meeting Link */}
          <div className={styles.meetingLinkContainer} id="meeting-link-container">
            <div className={styles.meetingLinkDisplay} id="meeting-link">
              {meetingLink}
            </div>
            <OTooltip title="Copy">
              <IconButton
                onClick={() => copyMessageToClipboard(meetingLink, 'Link copied successfully')}
                id="copy-button"
              >
                <MdContentCopy className={styles.copyBtn} />
              </IconButton>
            </OTooltip>
          </div>
        </div>
      </OResponsiveModal>

      {/* Mobile Filter Modal */}
      {isMobile && FilterModal}
    </div>
  );
};

export default ScheduledMeetingsView;
